import React, { useState, forwardRef } from 'react';
import { createPortal } from 'react-dom';
import DatePicker from 'react-datepicker';
import dayjs from 'dayjs';
import "react-datepicker/dist/react-datepicker.css";

interface DateRangePickerProps {
    className?: string;
    onApply?: (start: dayjs.Dayjs, end: dayjs.Dayjs) => void;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({ className = '', onApply }) => {
    const [startDate, setStartDate] = useState<Date | null>(dayjs().subtract(6, 'day').toDate());
    const [endDate, setEndDate] = useState<Date | null>(dayjs().toDate());

    const handleDateChange = (dates: [Date | null, Date | null]) => {
        const [start, end] = dates;
        setStartDate(start);
        setEndDate(end);

        if (start && end && onApply) {
            onApply(dayjs(start), dayjs(end));
        }
    };

    const handleRangeClick = (range: string) => {
        let start = dayjs();
        let end = dayjs();

        switch (range) {
            case 'Today':
                start = dayjs();
                end = dayjs();
                break;
            case 'Yesterday':
                start = dayjs().subtract(1, 'day');
                end = dayjs().subtract(1, 'day');
                break;
            case 'Last 7 Days':
                start = dayjs().subtract(6, 'day');
                end = dayjs();
                break;
            case 'Last 30 Days':
                start = dayjs().subtract(29, 'day');
                end = dayjs();
                break;
            case 'This Month':
                start = dayjs().startOf('month');
                end = dayjs().endOf('month');
                break;
            case 'Last Month':
                start = dayjs().subtract(1, 'month').startOf('month');
                end = dayjs().subtract(1, 'month').endOf('month');
                break;
        }

        setStartDate(start.toDate());
        setEndDate(end.toDate());

        if (onApply) {
            onApply(start, end);
        }
    };

    const ranges = [
        'Today',
        'Yesterday',
        'Last 7 Days',
        'Last 30 Days',
        'This Month',
        'Last Month'
    ];

    // Custom Input Component
    const CustomInput = forwardRef<HTMLDivElement, any>(({ onClick }, ref) => (
        <div className={`input-group ${className}`} onClick={onClick} ref={ref}>
            <div className={`form-control bg-transparent`} style={{ minWidth: '220px', cursor: 'pointer' }}>
                <span>
                    {startDate ? dayjs(startDate).format('MMM D, YYYY') : 'Start'} - {endDate ? dayjs(endDate).format('MMM D, YYYY') : 'End'}
                </span>
            </div>
            <i className="bi bi-calendar3 text-secondary input-group-text bg-transparent"></i>
        </div>
    ));

    // Custom Container to include Ranges
    const CalendarContainer = ({ className, children }: { className: string, children: React.ReactNode }) => {
        return (
            <div className={`card adminuiux-card d-flex overflow-hidden ${className}`}>
                <div className="card-body px-0 py-2" style={{ minWidth: '140px' }}>
                    <div className='row gx-0 flex-nowrap'>
                        <div className='col-auto d-flex flex-column'>
                            {ranges.map(range => (
                                <button
                                    key={range}
                                    className="btn btn-sm btn-link text-start mb-1"
                                    onClick={() => handleRangeClick(range)}
                                    style={{ fontSize: '0.875rem' }}
                                >
                                    {range}
                                </button>
                            ))}
                        </div>
                        <div className='col position-relative d-flex flex-nowrap'>{children}</div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="position-relative d-inline-block">
            <DatePicker
                selected={startDate}
                onChange={handleDateChange}
                startDate={startDate}
                endDate={endDate}
                selectsRange
                monthsShown={2}
                customInput={<CustomInput />}
                calendarContainer={CalendarContainer}
                popperContainer={({ children }) => createPortal(children, document.body)}
                dateFormat="MMM d, yyyy"
                shouldCloseOnSelect={false}
                popperPlacement="bottom-end"
            />
        </div>
    );
};

export default DateRangePicker;
